import { useAuthStore } from '@/Utility/auth/Auth';
import { type CategoryType, categoryTraversal } from '@/Utility/categories';
import { getCookie, setCookie } from '@/Utility/cookie';
import { useAppStore } from '@/inertia/AppStore';
import { TelemetryRunner } from './index';
import { TelemetrySyncSession } from './syncSession';

const NEW_VISITOR_COOKIE = 'zngly/__nvt';
const LOGGED_IN_USER_COOKIE = 'zngly/__liu';

// biome-ignore lint/complexity/noStaticOnlyClass: I want this as a class
export class Analytics {
  public static userData = (): Partial<AnalyticsData> => {
    const user = useAuthStore.getState().user;

    return {
      clientId: Analytics.getClientId(),
      // user
      userType: !!getCookie(NEW_VISITOR_COOKIE) ? 'new' : 'returning',
      loggedInUserId: this.getLoggedInUserCookie(user),
    };
  };

  public static data = (): Partial<AnalyticsData> => {
    const { playerNode, znglist, pageType, hub, isAppPreview } = useAppStore.getState();
    if (isAppPreview) return {};

    // 23 event params currently
    // Be careful about adding more event params: "Analytics doesn't process the 26th user property"
    // https://support.google.com/analytics/answer/9267744?hl=en
    const data: AnalyticsData = {
      // playerNode
      title: playerNode?.title,
      url: playerNode?.link,
      mediaType: playerNode?.znglyContent?.contentMediaName?.toLowerCase?.(),
      categories: Analytics.categories(),
      pageType: pageType,
      content_id: playerNode?.id,
      author: playerNode?.author?.node?.name, // @todo: retire at the end of 2025
      authorId: playerNode?.author?.node?.id,

      // znglist
      znglistId: znglist?.id,
      znglistTitle: znglist?.title,
      znglistAuthor: znglist?.author?.node?.name, // @todo: retire at the end of 2025
      znglistAuthorId: znglist?.author?.node?.id,

      // hub
      hubId: hub?.id,
      hubTitle: hub?.title,
      hubAuthor: hub?.author?.node?.name, // @todo: retire at the end of 2025
      hubAuthorId: hub?.author?.node?.id,
      hubGroup: hub?.group,

      // clientId
      clientId: Analytics.getClientId(),
      // user data
      ...Analytics.userData(),
    };

    // remove any null values
    for (const key in data) {
      const k = key as keyof AnalyticsData;
      if (data?.[k] === null || data?.[k] === undefined || data?.[k] === '') delete data[k];
    }

    return data;
  };

  /**
   * Get formatted categories for analytics
   * "(Parent - Child) (Parent - Child)"
   */
  private static categories(): string | null {
    const search = new URLSearchParams(window.location.search);

    if (!search.has('categories')) return null;

    let catIds = search.get('categories');

    try {
      catIds = JSON.parse(catIds ?? '');
    } catch (e) {
      return null;
    }
    if (!Array.isArray(catIds)) return null;

    const categories = useAppStore.getState().categories;
    if (!categories?.edges) return null;

    const catNames = categoryTraversal(categories.edges as unknown as CategoryType, (cat) => {
      if (catIds.indexOf(cat?.id ?? '') > -1) return cat;
    })
      .filter((item) => !!item)
      .sort((a, b) => (a?.name ?? '').localeCompare(b?.name ?? ''));

    if (catNames.length === 0) return null; // return null if there are no valid categories

    return catNames.map((cat) => `(${cat?.parent?.name ?? ''} - ${cat?.name ?? ''})`).join(' ');
  }

  /**
   * Get the logged in user cookie
   * Set the cookie if it doesn't exist or if the user id has changed
   */
  private static getLoggedInUserCookie = (
    user: ReturnType<(typeof useAuthStore)['getState']>['user']
  ): string | null => {
    const cookieVal = getCookie<string>(LOGGED_IN_USER_COOKIE);
    if (!!user?.id) {
      if (cookieVal !== user.id) {
        setCookie(LOGGED_IN_USER_COOKIE, user.id, { days: 7 });
      }

      return user.id;
    }

    if (!!cookieVal && cookieVal !== '') return cookieVal;

    return null;
  };

  public static userPush = (eventName: EventNameUser, eventParams: EventParamsUser) => {
    const userData = Analytics.userData();

    Analytics.gtagPush(eventName, {
      ...eventParams,
      ...userData,
    });
  };

  // @ts-ignore
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  public static getCatalogData = (node: any): Partial<AnalyticsData> => {
    return {
      clientId: Analytics.getClientId(),

      content_id: node?.id,
      title: node?.title,
      author: node?.author?.node?.name,
      authorId: node?.author?.node?.id,
    };
  };

  // @ts-ignore
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  public static getZnglistData = (node: any): Partial<AnalyticsData> => {
    return {
      clientId: Analytics.getClientId(),

      znglistId: node?.id,
      znglistAuthor: node?.author?.node?.name,
      znglistAuthorId: node?.author?.node?.id,
      znglistTitle: node?.title,
    };
  };

  // @ts-ignore
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  public static getHubData = (node: any): Partial<AnalyticsData> => {
    return {
      clientId: Analytics.getClientId(),

      hubId: node?.id,
      hubAuthor: node?.author?.node?.name,
      hubAuthorId: node?.author?.node?.id,
      hubTitle: node?.title,
      hubGroup: node?.group,
    };
  };

  // @ts-ignore
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  public static getContactData = (node: any): Partial<AnalyticsData> => {
    return {
      clientId: Analytics.getClientId(),

      contactId: node?.id,
    };
  };

  public static push = (eventName: EventName, eventParams: EventParams, pushDefaultData = true) => {
    if (pushDefaultData === false) {
      Analytics.gtagPush(eventName, eventParams);
      return;
    }

    const data = Analytics.data();

    // if no there's data return
    if (Object.keys(data).length === 0) return;

    Analytics.gtagPush(eventName, {
      ...eventParams,
      ...data,
    });
  };

  public static getClientId(): string {
    const match = document.cookie.match(/ga=(.+?);/);

    let clientId = '';

    if (!!match && match.length)
      for (const item of match) {
        if (item.includes('GA') && !item.includes('ga=')) {
          clientId = item;
          break;
        }
      }

    if (!clientId || clientId === '') {
      clientId = Analytics.generateGaCookie();
    }

    return clientId;
  }

  // cache the generated client id
  private static generatedClientId: string | null = null;

  /**
   * Generate a new GA cookie and set it in the browser - used to bypass adblockers/cookie restrictions
   * Since we are using this cookie for more than just analytics, it is imperative that it is set
   */
  // code sample from: analytics.js release 2017-09-21
  private static generateGaCookie(): string {
    if (Analytics.generatedClientId) return Analytics.generatedClientId;

    const randomGen = () => {
      const array = new Uint32Array(1);
      window.crypto.getRandomValues(array);
      const random = array[0] & 2147483647;

      return Number(random.toString().slice(0, 10));
    };

    const hashString = (str: string) => {
      let b = 1;
      let c: number;

      if (str) {
        for (b = 0, c = str.length - 1; 0 <= c; c--) {
          let d = str.charCodeAt(c);
          b = ((b << 6) & 268435455) + d + (d << 14);
          d = b & 266338304;
          b = 0 !== d ? b ^ (d >> 21) : b;
        }
        return b;
      }

      return 1;
    };

    const generateRandomClientId = () => {
      let a =
        window.navigator.userAgent +
        (document.cookie ? document.cookie : '') +
        (document.referrer ? document.referrer : '');
      let b = a.length;
      let c = window.history.length;

      for (a; 0 < c; ) {
        a += c-- ^ b++;
      }

      return [randomGen() ^ (hashString(a) & 2147483647), Math.round(new Date().getTime() / 1e3)].join('.');
    };

    const clientId = generateRandomClientId();
    const cookie = `GA1.1.${clientId}`;

    Analytics.generatedClientId = cookie;

    // set the _ga cookie
    setCookie('_ga', cookie, {
      days: 365,
      domain: 'domain',
    });

    // because we are setting the cookie, we need to set the new visitor type
    setCookie(NEW_VISITOR_COOKIE, 'true', { days: 0, hours: 0.5, domain: 'domain' }); // new visitor type (nvt)

    setTimeout(() => {
      TelemetryRunner.addGtagLoadedFunction(() => {}); // add empty function to gtag loaded -> trigger check for gtag loaded
      TelemetrySyncSession.getInstance().syncIntegrations();
    }, 2000);

    return cookie;
  }

  private static gtagPush = (
    eventName: EventName | EventNameUser,
    eventParams: (EventParamsUser | EventParams) & AnalyticsData
  ) => {
    // default value to eventaction
    if (!('value' in eventParams) && 'eventaction' in eventParams) {
      eventParams.value = eventParams.eventaction;
    }

    // @todo: umami
    if (typeof window?.umami?.track === 'function') {
      umami.track(eventName, eventParams);
    }

    // @ts-ignore
    if (typeof window?.znca === 'function') {
      // @ts-ignore
      window.znca({
        human_timestamp: new Date().toLocaleString('en-GB', { timeZone: 'UTC' }),
        eventName,
        timestamp: new Date().getTime(),
        refferer: document.referrer,
        page_title: document.title,
        page_location: window.location.href,
        page_path: window.location.pathname,
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        user_language: navigator.language,
        user_agent: navigator.userAgent,

        // event data
        ...eventParams,
      });
    }

    // remove any null values
    for (const key in eventParams) {
      const k = key as keyof AnalyticsData;
      if (eventParams?.[k] === null || eventParams?.[k] === undefined || eventParams?.[k] === '') delete eventParams[k];
    }

    if (typeof window?.gtag !== 'function') {
      window.gtag = (...args: unknown[]) => {
        window?.dataLayer?.push(args);
      };
    }

    if (TelemetryRunner.getHasGtagLoaded() === false) {
      TelemetryRunner.addGtagLoadedFunction(() => {
        gtag('event', eventName, eventParams);
      });
      return;
    }

    // push to gtag
    gtag('event', eventName, eventParams);
  };
}

export type AnalyticsData = {
  // catalog post
  title?: string | null;
  url?: string | null;
  duration?: number | null;
  mediaType?: string | null;
  categories?: string | null;
  pageType?: string | null;
  content_id?: string | null;
  author?: string | null;
  authorId?: string | null;

  // user
  clientId?: string | null;
  userType?: 'new' | 'returning' | null;
  loggedInUserId?: string | null;

  // znglist
  znglistTitle?: string | null;
  znglistAuthor?: string | null;
  znglistAuthorId?: string | null;
  znglistId?: string | null;

  // hubTypes
  hubId?: string | null;
  hubTitle?: string | null;
  hubAuthor?: string | null;
  hubAuthorId?: string | null;
  hubGroup?: string | null;

  // contact
  contactId?: string | null;

  // form
  formId?: string | null;
};

// progress is an interval of 10 from progress10% to progress100%
export type ProgressType =
  | 'progress10%'
  | 'progress20%'
  | 'progress30%'
  | 'progress40%'
  | 'progress50%'
  | 'progress60%'
  | 'progress70%'
  | 'progress80%'
  | 'progress90%'
  | 'progress100%';

export type EventAction =
  | 'play'
  | ProgressType
  | 'view'
  | 'share'
  | 'search'
  | 'select'
  | 'click'
  | 'cta_click'
  | 'form_submission'
  | 'login'
  | 'view_content';

type EventName = Gtag.EventNames | 'zngly_media_interaction' | 'zngly_content_view' | 'zngly_inbound' | 'zngly_form';

export type EventParams = {
  [key: string]: string | number | boolean | string[] | number[] | boolean[] | null | undefined;
  value?: string;
  eventaction?: EventAction;
  eventlabel?: string;
};

// User Interaction

type EventNameUser = 'login' | 'user_dash_action';

type PostTypes = 'catalog' | 'znglist' | 'hub' | 'contact' | 'user' | 'form';

type EventActionUser = `create_${PostTypes}` | `update_${PostTypes}` | `delete_${PostTypes}` | 'update_user_password';

type EventParamsUser = {
  [key: string]: string | number | boolean | string[] | number[] | boolean[] | null | undefined;
  value?: string | null;
  eventaction?: EventActionUser;
} & Partial<AnalyticsData>;
